import {
    Directive,
    Inject,
    Input,
    OnDestroy,
    OnInit,
    Optional,
    PLATFORM_ID,
    ViewContainerRef,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { fromEvent, Subscription } from 'rxjs';
import { bufferTime, filter, map } from 'rxjs/operators';
import { StickyAnchorContainerDirective } from './sticky-anchor-container.directive';
import { NavigationEnd, Router } from '@angular/router';

/**
 * @description
 * Implements a "position: sticky" behaviour but without the limitation of being positioned relative
 * to the immediate block parent. This allows us to implement a sticky section relative the document
 * scrolling, yet still bounded by a selected ancestor container.
 */
@Directive({
    selector: '[kbStickyAnchor]',
    standalone: true,
})
export class StickyAnchorDirective implements OnInit, OnDestroy {
    @Input() minWidth = 1024;
    private subscription: Subscription;

    get element(): HTMLElement {
        return this.viewContainerRef.element.nativeElement;
    }

    constructor(
        private viewContainerRef: ViewContainerRef,
        private router: Router,
        @Inject(PLATFORM_ID) private platformId: any,
        @Optional() private container?: StickyAnchorContainerDirective,
    ) {}

    ngOnInit() {
        const element = this.element;
        const child = element.firstChild;
        const container = this.container?.element;

        if (isPlatformBrowser(this.platformId)) {
            const scrollY$ = fromEvent(window, 'scroll', { passive: true }).pipe(map(() => window.scrollY));
            const deltaY$ = scrollY$.pipe(
                bufferTime(50),
                filter((val) => 1 < val.length),
                map((val) => val[val.length - 1] - val[0]),
            );
            this.subscription = deltaY$.subscribe((deltaY) => {
                if (child && child instanceof HTMLElement) {
                    if (window.innerWidth < this.minWidth) {
                        this.resetChild(child);
                        return;
                    }
                    const childHeight = child.getBoundingClientRect().height;
                    if (window.innerHeight * 0.7 < childHeight) {
                        this.resetChild(child);
                        return;
                    }
                    const scrollOffsetY = window.scrollY;
                    const anchorTop = element.getBoundingClientRect().top + scrollOffsetY;
                    const containerBottom = container
                        ? container.getBoundingClientRect().bottom + scrollOffsetY
                        : document.body.clientHeight;
                    const containerHeight = container ? container.getBoundingClientRect().height : document.body.clientHeight;
                    const sufficientSpaceToFloat = containerHeight > childHeight + 150; // 150px buffer
                    const headerHeight =
                        deltaY < 0
                            ? document.querySelector('vsf-layout-header')?.getBoundingClientRect().height ?? 0
                            : 0;
                    child.style.transition = 'top 0.2s';
                    if (sufficientSpaceToFloat && containerBottom <= scrollOffsetY + childHeight + headerHeight) {
                        // We've scrolled lower than the container allows,
                        // so fix the child to the bottom of the container
                        child.classList.remove('fixed');
                        child.classList.add('bottom-0', 'absolute');
                        child.style.top = '';
                    } else if (sufficientSpaceToFloat && anchorTop - headerHeight < scrollOffsetY) {
                        // We've started scrolling within the container, so
                        // fix the child to the top.
                        child.classList.add('fixed');
                        child.classList.remove('absolute', 'bottom-0');
                        // Keep the child width as it was when in the natural position
                        child.style.width = element.getBoundingClientRect().width + 'px';
                        // Account for the header taking up the top portion
                        if (deltaY < 0) {
                            child.style.top = headerHeight != null ? `${headerHeight}px` : '120px';
                        } else {
                            child.style.top = '0';
                        }
                    } else {
                        // Return element to natural position
                        this.resetChild(child);
                    }
                }
            });
            this.subscription.add(
                this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
                    if (child && child instanceof HTMLElement) {
                        this.resetChild(child);
                    }
                }),
            );
        }
    }

    ngOnDestroy() {
        this.subscription?.unsubscribe();
    }

    private resetChild(child: HTMLElement) {
        child.classList.remove('fixed', 'absolute', 'top-0', 'bottom-0');
        child.style.width = '';
        child.style.top = '0';
    }
}
